/**
 * Hook for managing QMS assignment prompt when moving tickets to "In Progress"
 * Shows a dialog prompting the user to optionally assign someone before proceeding
 */

import { useState, useCallback } from 'react';
import type { QmsAssignmentPromptDialogProps } from '../components/shared/QmsAssignmentPromptDialog';
import type { QmsTicket, QmsTicketStatus } from '../types/qms.types';
import { extractId } from '../types/qms.types';

interface PendingMove {
  ticket: QmsTicket;
  newStatus: QmsTicketStatus;
  onProceed: (assignedUserId?: string) => Promise<void>;
}

interface UseQmsAssignmentPromptReturn {
  /** Props to spread onto QmsAssignmentPromptDialog */
  dialogProps: Omit<QmsAssignmentPromptDialogProps, 'isLoading'>;
  /** Whether an assignment operation is in progress */
  isAssigning: boolean;
  /**
   * Check if an assignment prompt is needed and show it if so.
   * If no prompt is needed, calls onProceed immediately.
   *
   * @returns true if prompt was shown, false if onProceed was called directly
   */
  promptForAssignment: (params: {
    ticket: QmsTicket;
    newStatus: QmsTicketStatus;
    onProceed: (assignedUserId?: string) => Promise<void>;
  }) => boolean;
}

/**
 * Hook that manages the assignment prompt flow when moving tickets to "In Progress"
 *
 * @example
 * const { dialogProps, isAssigning, promptForAssignment } = useQmsAssignmentPrompt();
 *
 * // In your drag handler or status change:
 * const shouldShowPrompt = promptForAssignment({
 *   ticket,
 *   newStatus: 'in_progress',
 *   onProceed: async (assignedUserId) => {
 *     // Optional: update assignment first
 *     if (assignedUserId) {
 *       await updateTicket({ assigned_to: assignedUserId });
 *     }
 *     // Then proceed with the status change
 *     await moveTicket({ status: 'in_progress' });
 *   },
 * });
 *
 * // Render the dialog:
 * <QmsAssignmentPromptDialog {...dialogProps} isLoading={isAssigning} />
 */
export function useQmsAssignmentPrompt(): UseQmsAssignmentPromptReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);

  /**
   * Check if ticket needs assignment prompt:
   * - Moving to 'in_progress' status
   * - Ticket is currently unassigned
   */
  const needsAssignmentPrompt = useCallback((ticket: QmsTicket, newStatus: QmsTicketStatus): boolean => {
    if (newStatus !== 'in_progress') {
      return false;
    }

    const assignedToId = extractId(ticket.assigned_to);
    return !assignedToId;
  }, []);

  /**
   * Check if an assignment prompt is needed and show it if so.
   * If no prompt is needed, calls onProceed immediately.
   */
  const promptForAssignment = useCallback(
    (params: {
      ticket: QmsTicket;
      newStatus: QmsTicketStatus;
      onProceed: (assignedUserId?: string) => Promise<void>;
    }): boolean => {
      const { ticket, newStatus, onProceed } = params;

      if (!needsAssignmentPrompt(ticket, newStatus)) {
        // No prompt needed - proceed immediately
        onProceed();
        return false;
      }

      // Show the prompt
      setPendingMove({ ticket, newStatus, onProceed });
      setIsOpen(true);
      return true;
    },
    [needsAssignmentPrompt]
  );

  /**
   * Handle user choosing to assign someone
   */
  const handleAssign = useCallback(async (userId: string) => {
    if (!pendingMove) return;

    setIsAssigning(true);
    try {
      await pendingMove.onProceed(userId);
      setIsOpen(false);
      setPendingMove(null);
    } catch (error) {
      // Error is handled by the caller (mutation's onError)
      // Keep dialog open so user can retry or skip
      console.error('Failed to assign and move ticket:', error);
    } finally {
      setIsAssigning(false);
    }
  }, [pendingMove]);

  /**
   * Handle user choosing to skip assignment
   */
  const handleSkip = useCallback(async () => {
    if (!pendingMove) return;

    setIsAssigning(true);
    try {
      await pendingMove.onProceed(undefined);
      setIsOpen(false);
      setPendingMove(null);
    } catch (error) {
      // Error is handled by the caller (mutation's onError)
      // Keep dialog open so user can retry
      console.error('Failed to move ticket:', error);
    } finally {
      setIsAssigning(false);
    }
  }, [pendingMove]);

  /**
   * Handle dialog close (e.g., clicking outside or pressing escape)
   */
  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      // Dialog was closed without action - don't proceed
      setIsOpen(false);
      setPendingMove(null);
    }
  }, []);

  const dialogProps: Omit<QmsAssignmentPromptDialogProps, 'isLoading'> = {
    open: isOpen,
    onOpenChange: handleOpenChange,
    onAssign: handleAssign,
    onSkip: handleSkip,
  };

  return {
    dialogProps,
    isAssigning,
    promptForAssignment,
  };
}
